import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Brain, CalendarDays, FileText, Upload, Shield, User, Clock, Stethoscope, Activity, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function PatientJourneyPage() {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [journey, setJourney] = useState({ appointments: [], triages: [], files: [], forms: [] });
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState('insurance');
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    API.get('/patients?limit=200').then(res => setPatients(res.data.patients || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  const loadJourney = async (patient) => {
    setSelectedPatient(patient);
    try {
      const [appts, files, forms] = await Promise.all([
        API.get(`/appointments/patient/${patient.id}`),
        API.get(`/uploads/patient/${patient.id}`),
        API.get(`/surveys/submissions/list?patient_id=${patient.id}`)
      ]);
      setJourney({
        appointments: appts.data.appointments || [],
        files: files.data.files || [],
        forms: forms.data.submissions || []
      });
    } catch (err) { console.error(err); }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedPatient) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('patient_id', selectedPatient.id);
    formData.append('document_type', uploadType);
    try {
      await API.post('/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setUploadDialog(false);
      setUploadFile(null);
      loadJourney(selectedPatient);
    } catch (err) { toast.error('Upload failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="flex h-full" data-testid="patient-journey-page">
      <div className="w-72 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Patients</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {patients.map(p => (
            <button key={p.id} data-testid={`journey-patient-${p.id}`} onClick={() => loadJourney(p)}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 transition-colors ${selectedPatient?.id === p.id ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
              <p className="text-sm font-medium text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-500">{p.gender} | DOB: {p.date_of_birth}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {selectedPatient ? (
          <div className="p-6 lg:p-8 space-y-6 max-w-[1000px]">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{selectedPatient.name}</h1>
                <p className="text-sm text-slate-500">{selectedPatient.email} | {selectedPatient.phone} | {selectedPatient.medical_history || 'No history'}</p>
              </div>
              <Button data-testid="upload-document-btn" variant="outline" onClick={() => setUploadDialog(true)}>
                <Upload className="w-4 h-4 mr-2" /> Upload Document
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="stat-card"><p className="text-sm text-slate-500">Appointments</p><p className="text-2xl font-bold text-slate-900">{journey.appointments.length}</p></div>
              <div className="stat-card"><p className="text-sm text-slate-500">Documents</p><p className="text-2xl font-bold text-slate-900">{journey.files.length}</p></div>
              <div className="stat-card"><p className="text-sm text-slate-500">Forms Filled</p><p className="text-2xl font-bold text-slate-900">{journey.forms.length}</p></div>
            </div>

            {journey.files.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-2"><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Documents</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {journey.files.map(f => (
                      <div key={f.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Shield className="w-4 h-4 text-sky-600" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{f.original_name}</p>
                            <p className="text-xs text-slate-500 capitalize">{f.document_type?.replace('_', ' ')} | {(f.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="capitalize text-xs">{f.document_type}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Journey Timeline</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {journey.appointments.length === 0 && journey.forms.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No journey events yet</p>
                  ) : (
                    <>
                      {journey.appointments.map(appt => (
                        <div key={appt.id} className="flex gap-4 p-3 rounded-lg bg-slate-50">
                          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                            <CalendarDays className="w-5 h-5 text-sky-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-slate-900">Appointment with {appt.doctor_name}</p>
                              <Badge className={appt.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : appt.status === 'confirmed' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}>{appt.status}</Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{appt.date || 'Date TBD'} {appt.time && `at ${appt.time}`}</p>
                            {appt.form_submission_id && <Badge variant="secondary" className="mt-1 text-xs"><FileText className="w-3 h-3 mr-1" />Form submitted</Badge>}
                          </div>
                        </div>
                      ))}
                      {journey.forms.map(form => (
                        <div key={form.id} className="flex gap-4 p-3 rounded-lg bg-slate-50">
                          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{form.survey_name || 'Form Submission'}</p>
                            <p className="text-xs text-slate-500">{new Date(form.submitted_at).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Upload Document</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Document Type</Label>
                    <Select value={uploadType} onValueChange={setUploadType}>
                      <SelectTrigger data-testid="upload-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="insurance">Insurance Card</SelectItem>
                        <SelectItem value="passport">Passport</SelectItem>
                        <SelectItem value="id_card">ID Card</SelectItem>
                        <SelectItem value="medical_report">Medical Report</SelectItem>
                        <SelectItem value="lab_results">Lab Results</SelectItem>
                        <SelectItem value="prescription">Prescription</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>File</Label>
                    <Input type="file" onChange={e => setUploadFile(e.target.files?.[0])} data-testid="upload-file-input" />
                  </div>
                  <Button onClick={handleUpload} disabled={!uploadFile} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="upload-submit">Upload</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-sm">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Patient Journey</h3>
              <p className="text-sm text-slate-500">Select a patient to view their complete healthcare journey including appointments, forms, and documents.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
